/**
 * Homeowner chat transcript of a registration-anchored service case (plan D8), read through
 * `get_service_case_chat_transcript(p_case_id)`: one row per message, plus one row with NULL
 * message columns for a linked session that has no message yet. The generated types mark every
 * column non-null (RETURNS TABLE), so the row type here restates the real nullability.
 */
export interface ServiceCaseTranscriptRow {
    session_id: string;
    session_created_at: string;
    session_status: string | null;
    session_outcome: string | null;
    session_closed_at: string | null;
    session_hazard_reported_at: string | null;
    message_id: string | null;
    message_role: string | null;
    message_content: string | null;
    message_input_mode: string | null;
    message_answer_basis: string | null;
    message_answer_status: string | null;
    message_safety_level: string | null;
    message_risk_class: string | null;
    message_created_at: string | null;
}

export interface ServiceCaseTranscriptMessage {
    id: string;
    role: 'USER' | 'ASSISTANT' | 'SYSTEM';
    content: string;
    inputMode: string | null;
    answerBasis: string | null;
    answerStatus: string | null;
    safetyLevel: string | null;
    riskClass: string | null;
    createdAt: string | null;
}

export interface ServiceCaseTranscriptSession {
    id: string;
    createdAt: string;
    status: string | null;
    outcome: string | null;
    closedAt: string | null;
    hazardReportedAt: string | null;
    messages: ServiceCaseTranscriptMessage[];
}

export const CHAT_SESSION_OUTCOME_LABELS: Record<string, string> = {
    RESOLVED_SELF: 'Resolved by the homeowner',
    UNRESOLVED: 'Unresolved',
    ESCALATED_MANUFACTURER: 'Escalated to the manufacturer',
    ESCALATED_WARRANTY_CLAIM: 'Escalated to a warranty claim',
    ESCALATED_FOLLOW_ON_JOB: 'Escalated to a follow-on job',
    ESCALATED_VARIATION: 'Escalated to a variation',
    ABANDONED: 'Abandoned',
};

function normaliseRole(role: string | null): ServiceCaseTranscriptMessage['role'] {
    const upper = String(role ?? '').toUpperCase();
    if (upper === 'USER' || upper === 'SYSTEM') return upper;
    return 'ASSISTANT';
}

/** Rows (already ordered by the RPC: session, then message time) grouped into sessions. */
export function groupTranscriptRows(rows: readonly ServiceCaseTranscriptRow[]): ServiceCaseTranscriptSession[] {
    const sessions: ServiceCaseTranscriptSession[] = [];
    const byId = new Map<string, ServiceCaseTranscriptSession>();

    for (const row of rows) {
        if (!row || typeof row.session_id !== 'string') continue;
        let session = byId.get(row.session_id);
        if (!session) {
            session = {
                id: row.session_id,
                createdAt: row.session_created_at,
                status: row.session_status ?? null,
                outcome: row.session_outcome ?? null,
                closedAt: row.session_closed_at ?? null,
                hazardReportedAt: row.session_hazard_reported_at ?? null,
                messages: [],
            };
            byId.set(row.session_id, session);
            sessions.push(session);
        }
        if (row.message_id) {
            session.messages.push({
                id: row.message_id,
                role: normaliseRole(row.message_role),
                content: row.message_content ?? '',
                inputMode: row.message_input_mode ?? null,
                answerBasis: row.message_answer_basis ?? null,
                answerStatus: row.message_answer_status ?? null,
                safetyLevel: row.message_safety_level ?? null,
                riskClass: row.message_risk_class ?? null,
                createdAt: row.message_created_at ?? null,
            });
        }
    }

    return sessions;
}
